import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import { Button } from "@src/components/atoms/buttons/button/button";
import { Input } from "@components/atoms/input/input";

import CloseIcon from "@src/assets/svg/close-icon.svg";

import {
  updateMyBlueprintLibraryModalContentShown,
  updateSelectedMyBlueprintLibraryBlueprint,
  updateShowMyBlueprintLibraryModal,
} from "@src/reducers/your-reducer";

import styles from "./find-my-blueprint-library-modal-content.module.scss";
import { FlexRowContainer } from "@src/components/templates/layouts/flex-row-container/flex-row-container";
import { findMatchingBlueprintsByName } from "../../helpers/find-matching-blueprints-by-name";
import { renderBlueprintPopover } from "../../helpers/render-blueprint-popover";
import { handleCloseDescriptionPopover } from "../../helpers/handle-close-description-popover";
import { HorizontalSeparator } from "@src/components/atoms/horizontal-separator/horizontal-separator";
import { GlassyModalHeader } from "@src/components/molecules/glassy-modal-header/glassy-modal-header";
import { BlueprintTableWithSpinner } from "@src/components/organisms/blueprint-table-with-spinner/blueprint-table-with-spinner";
import { getLogger } from "@src/cloud-connect/dependency-injection-container/get-logger";
import { Help } from "@src/cloud-connect/help/entities/help";
import { HelpRepository } from "@src/cloud-connect/help/repositories/help-repository";
import { getMyBlueprintLibraryUseCase } from "@src/cloud-connect/blueprint/use-cases/get-suggested-blueprints-use-case";
import { useI18N } from "@src/language/language-context";

const cssClassName = "find-my-blueprint-library-modal-content";

export const FindMyBlueprintLibraryModalContent = () => {
  const logger = getLogger();
  const dispatch = useDispatch();
  const i18n = useI18N();

  const [searchValue, setSearchValue] = useState("");
  const [suggestedBlueprintsArray, setSuggestedBlueprintsArray] = useState([]);
  const [initialSuggestedBlueprintArray, setInitialSuggestedBlueprintArray] =
    useState([]);
  const [popoverPosition, setPopoverPosition] = useState({ x: 0, y: 0 });
  const [activePopover, setActivePopover] = useState(null);
  const [activeBlueprint, setActiveBlueprint] = useState(null);
  const [loadingBlueprintsList, setLoadingBlueprintsList] = useState(true);
  const modalRef = useRef<HTMLDivElement>(null);

  const texts = {
    subTitle: i18n("modules.findMyBlueprintLibraryModal.subTitle"),
    inputPlaceholder: i18n(
      "modules.findMyBlueprintLibraryModal.inputPlaceholder",
    ),
    blueprint: i18n("modules.findMyBlueprintLibraryModal.blueprint"),
    blueprintLibrary: i18n(
      "modules.findMyBlueprintLibraryModal.blueprintLibrary",
    ),
    applyButton: i18n("modules.findMyBlueprintLibraryModal.applyButton"),
    cancelButton: i18n("modules.findMyBlueprintLibraryModal.cancelButton"),
    blueprintSupportText: i18n(
      "modules.findMyBlueprintLibraryModal.blueprintSupportText",
    ),
    blueprintSupportLink: i18n(
      "modules.findMyBlueprintLibraryModal.blueprintSupportLink",
    ),
  };

  const closeModal = () => {
    dispatch(updateShowMyBlueprintLibraryModal(false));
  };

  const handleInputOnChange = (e) => {
    setSearchValue(e.target.value);
  };

  const handleApplyBlueprint = () => {
    dispatch(updateMyBlueprintLibraryModalContentShown("apply"));
    dispatch(updateSelectedMyBlueprintLibraryBlueprint(activeBlueprint));
  };

  const loadSuggestedBlueprints = async () => {
    setLoadingBlueprintsList(true);

    const blueprints = await getMyBlueprintLibraryUseCase();
    logger.debug("loadSuggestedBlueprints", { blueprints: blueprints });

    setInitialSuggestedBlueprintArray(blueprints);
    setSuggestedBlueprintsArray(blueprints);

    setLoadingBlueprintsList(false);
  };

  const blueprintSupportLinkBuilder = () => {
    const help = new Help(new HelpRepository());
    return help.getBlueprintHelpRequestUrl();
  };

  useEffect(() => {
    if (searchValue === "") {
      setSuggestedBlueprintsArray(initialSuggestedBlueprintArray);
    } else {
      setSuggestedBlueprintsArray(
        findMatchingBlueprintsByName(
          initialSuggestedBlueprintArray,
          searchValue,
        ),
      );
    }
  }, [searchValue, initialSuggestedBlueprintArray]);

  useEffect(() => {
    loadSuggestedBlueprints();
  }, []);

  return (
    <div ref={modalRef}>
      <GlassyModalHeader
        title={texts.subTitle}
        rightIcon={CloseIcon}
        rightIconFunction={closeModal}
      />
      <HorizontalSeparator verticalMargin={16} />
      <Input
        inputVariant={"dark-background"}
        labelText={texts.blueprint}
        inputPlaceHolder={texts.inputPlaceholder}
        inputValue={searchValue}
        onChangeFunc={handleInputOnChange}
      />
      <div>
        <div className={styles[`${cssClassName}__label`]}>
          {texts.blueprintLibrary}
        </div>
        <BlueprintTableWithSpinner
          ref={modalRef}
          loadingBlueprintsList={loadingBlueprintsList}
          suggestedBlueprintsArray={suggestedBlueprintsArray}
          activeBlueprint={activeBlueprint}
          setPopoverPosition={setPopoverPosition}
          setActiveBlueprint={setActiveBlueprint}
          setActivePopover={setActivePopover}
        />
      </div>
      <HorizontalSeparator verticalMargin={16} />
      <FlexRowContainer justifyContent="space-between">
        <FlexRowContainer>
          <Button
            fun={handleApplyBlueprint}
            text={texts.applyButton}
            variant={[!activeBlueprint ? "inactive" : ""]}
            disabled={!activeBlueprint}
          />
          <Button
            fun={closeModal}
            variant="secondary"
            text={texts.cancelButton}
          />
        </FlexRowContainer>
        <p className={styles[`${cssClassName}__text`]}>
          {texts.blueprintSupportText}{" "}
          <a
            className={styles[`${cssClassName}__link`]}
            rel="noreferrer"
            href={blueprintSupportLinkBuilder()}
          >
            {texts.blueprintSupportLink}
          </a>
        </p>
      </FlexRowContainer>
      {renderBlueprintPopover(activePopover, popoverPosition, (e) =>
        handleCloseDescriptionPopover(e, setActivePopover),
      )}
    </div>
  );
};
